import { useState, useEffect, useContext, Fragment, useRef } from "react";
import axios from "axios";
import {
	PaymentElement,
	useStripe,
	useElements,
	Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PropTypes from "prop-types";
import Button from "@components/Button";
import { FormContext } from "@providers/FormProvider";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";

// const stripePromise = loadStripe(
// 	"pk_test_51QZrAQH1aeFjzv3vaq7ffg94qg990G72S6yD2L029L00RjL51UDAFDE2fky5dvvPAJ9Bg5WjGj6LUUtplFNwFdK400NGJUs2ZK"
// );

const stripePromise = loadStripe(
	"pk_live_51QAVWwHHC1jWtrw9OQaUiTcTTm3lbjrs65BPgYXIVE8ZzF87W1XcEa9GXsT3LDWWKghYypoNlXspWRTGyNuZLRSW008k3GtMqQ"
);

const appearance = {
	theme: "flat",
	variables: {
		colorPrimary: "#2d3250",
		colorText: "#2d3250",
	},
};

const loader = "auto";

// Abandoned checkout timeout in milliseconds (5 minutes)
const ABANDONED_CHECKOUT_TIMEOUT = 5 * 60 * 1000;

function StripeCheckoutForm({
	totalPrice,
	isLoading,
	setIsLoading,
	formData,
	cartItems,
	setMessage,
	setIsModalVisible,
	navigate,
}) {
	const stripe = useStripe();
	const elements = useElements();

	const combinedData = {
		...formData,
		cartItems: cartItems.map((item) => ({
			title: item.title,
			price: `$${item.price.toFixed(2)}`,
			priceId: item.priceId,
		})),
		totalPrice: `$${totalPrice.toFixed(2)}`,
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setMessage(null);

		if (!stripe || !elements) {
			setMessage("Payment system is not ready. Please try again.");
			return;
		}

		setIsLoading(true);

		try {
			const { error, paymentIntent } = await stripe.confirmPayment({
				elements,
				confirmParams: {
					return_url: `${window.location.origin}/packages`,
					payment_method_data: {
						billing_details: {
							address: {
								country: "AU",
							},
						},
					},
				},
				redirect: "if_required",
			});

			if (error) {
				if (error.type === "card_error" || error.type === "validation_error") {
					setMessage(error.message);
				} else {
					setMessage("An unexpected error occurred.");
					console.error("Payment error:", error);
				}
			} else if (paymentIntent && paymentIntent.status === "succeeded") {
				setMessage("Payment successful!");
				setIsModalVisible(true);
				setTimeout(() => {
					setIsModalVisible(false);
					navigate("/");
				}, 2000);
				// Send email with form data
				axios
					.post("/sendEmail.php", combinedData)
					.then((response) => {
						console.log("Email sent successfully:", response.data);
					})
					.catch((error) => {
						console.error("Error sending email:", error.response.data);
					});
			}
		} catch (err) {
			console.error("Payment failed:", err);
			setMessage("Payment failed. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<PaymentElement
				options={{
					layout: "tabs",
					defaultValues: {
						billingDetails: {
							address: {
								country: "AU",
							},
						},
					},
				}}
			/>
			<div className="flex justify-center mt-6">
				<Button
					buttonName={
						isLoading ? (
							<span>Processing...</span>
						) : (
							<span>
								Pay ${totalPrice ? totalPrice.toFixed(2) : "0.00"} Now
							</span>
						)
					}
					disabled={isLoading || !stripe}
					buttonStyle="secondary"
					type="submit"
				/>
			</div>
		</form>
	);
}

function StripePaymentForm({ cartItems, formData }) {
	const { formData: contextFormData } = useContext(FormContext);
	const [clientSecret, setClientSecret] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [message, setMessage] = useState(null);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const navigate = useNavigate();

	// Refs to track checkout status
	const abandonedCheckoutTimerRef = useRef(null);
	const checkoutCompletedRef = useRef(false);
	const abandonedEmailSentRef = useRef(false);

	// Calculate total price
	const totalPrice = cartItems.reduce((total, item) => total + item.price, 0);

	// Function to send abandoned checkout email
	const sendAbandonedCheckoutEmail = () => {
		// Only send if checkout wasn't completed and email hasn't been sent yet
		if (!checkoutCompletedRef.current && !abandonedEmailSentRef.current) {
			abandonedEmailSentRef.current = true;

			const abandonedData = {
				...formData,
				cartItems: cartItems.map((item) => ({
					title: item.title,
					price: `$${item.price.toFixed(2)}`,
					priceId: item.priceId,
				})),
				totalPrice: `$${totalPrice.toFixed(2)}`,
				abandonmentTimestamp: new Date().toLocaleString(),
			};

			axios
				.post("/abandonedCheckoutEmail.php", abandonedData)
				.then((response) => {
					console.log("Abandoned checkout email sent:", response.data);
				})
				.catch((error) => {
					console.error("Error sending abandoned checkout email:", error);
				});
		}
	};

	// Set up abandoned checkout tracking
	useEffect(() => {
		// Clear any existing timer
		if (abandonedCheckoutTimerRef.current) {
			clearTimeout(abandonedCheckoutTimerRef.current);
		}

		// Only set up timer if we have cart items and form data
		if (cartItems.length > 0 && formData && clientSecret) {
			// Start timer for abandoned checkout
			abandonedCheckoutTimerRef.current = setTimeout(() => {
				sendAbandonedCheckoutEmail();
			}, ABANDONED_CHECKOUT_TIMEOUT);
		}

		// Cleanup function
		return () => {
			if (abandonedCheckoutTimerRef.current) {
				clearTimeout(abandonedCheckoutTimerRef.current);
			}
		};
	}, [cartItems, formData, clientSecret]);

	// Track page visibility and navigation
	useEffect(() => {
		const handleBeforeUnload = () => {
			// If user is leaving and hasn't completed checkout, send email immediately
			if (!checkoutCompletedRef.current && !abandonedEmailSentRef.current) {
				// Use sendBeacon for more reliable delivery when page is unloading
				const abandonedData = {
					...formData,
					cartItems: cartItems.map((item) => ({
						title: item.title,
						price: `$${item.price.toFixed(2)}`,
						priceId: item.priceId,
					})),
					totalPrice: `$${totalPrice.toFixed(2)}`,
					abandonmentTimestamp: new Date().toLocaleString(),
				};

				// Try to send with sendBeacon (more reliable on page unload)
				const blob = new Blob([JSON.stringify(abandonedData)], {
					type: "application/json",
				});
				navigator.sendBeacon("/abandonedCheckoutEmail.php", blob);
				abandonedEmailSentRef.current = true;
			}
		};

		const handleVisibilityChange = () => {
			if (
				document.hidden &&
				!checkoutCompletedRef.current &&
				!abandonedEmailSentRef.current
			) {
				// User switched tabs/minimized - trigger timer immediately
				if (abandonedCheckoutTimerRef.current) {
					clearTimeout(abandonedCheckoutTimerRef.current);
				}
				// Send email after 30 seconds of being hidden
				abandonedCheckoutTimerRef.current = setTimeout(() => {
					sendAbandonedCheckoutEmail();
				}, 30000);
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [cartItems, formData, totalPrice]);

	// Mark checkout as completed when payment succeeds
	useEffect(() => {
		if (message === "Payment successful!") {
			checkoutCompletedRef.current = true;
			// Clear the abandoned checkout timer
			if (abandonedCheckoutTimerRef.current) {
				clearTimeout(abandonedCheckoutTimerRef.current);
			}
		}
	}, [message]);

	useEffect(() => {
		setIsLoading(true);
		setError(null);

		axios
			.post("/create.php", {
				cartItems: cartItems.map((item) => ({
					priceId: item.priceId,
					title: item.title,
					price: item.price,
				})),
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				contact: formData.contact,
			})
			.then((response) => {
				setClientSecret(response.data.clientSecret);
			})
			.catch((error) => {
				console.error("Error creating PaymentIntent:", error);
				setError(
					error.response?.data?.error || "Payment initialization failed"
				);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [cartItems, formData]);

	if (error) {
		return <div className="text-red-500">Error: {error}</div>;
	}

	return (
		<div className="w-full max-w-md mx-auto">
			{clientSecret && (
				<Elements
					stripe={stripePromise}
					options={{
						clientSecret,
						appearance,
						loader,
					}}>
					<StripeCheckoutForm
						totalPrice={totalPrice}
						isLoading={isLoading}
						setIsLoading={setIsLoading}
						formData={formData || contextFormData}
						cartItems={cartItems}
						setMessage={setMessage}
						setIsModalVisible={setIsModalVisible}
						navigate={navigate}
					/>
				</Elements>
			)}

			{message && (
				<div className="text-center text-white bg-green-500 p-2 rounded mt-4">
					{message}
				</div>
			)}

			<Transition appear show={isModalVisible} as={Fragment}>
				<Dialog
					as="div"
					className="relative z-50"
					open={isModalVisible}
					onClose={() => setIsModalVisible(false)}>
					{/* The backdrop, rendered as a fixed sibling to the panel container */}
					<div className="fixed inset-0 bg-black/30" aria-hidden="true" />

					{/* Full-screen container to center the panel */}
					<div className="fixed inset-0 flex justify-center items-center p-4">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
							<Dialog.Panel className="bg-steerLightBlack mx-auto p-12 rounded max-w-lg">
								<Dialog.Title
									as="h3"
									className="font-poppins font-semibold text-4xl text-center text-white">
									Payment Successful!
								</Dialog.Title>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		</div>
	);
}

StripeCheckoutForm.propTypes = {
	totalPrice: PropTypes.number,
	isLoading: PropTypes.bool,
	setIsLoading: PropTypes.func,
	formData: PropTypes.object,
	cartItems: PropTypes.array,
	setMessage: PropTypes.func,
	setIsModalVisible: PropTypes.func,
	navigate: PropTypes.func,
};

StripePaymentForm.propTypes = {
	cartItems: PropTypes.array,
	formData: PropTypes.object,
};

export default StripePaymentForm;
